// Types pour le module Fournisseurs KOPRO
// Pattern identique à units/types.ts : re-export des types de base + types étendus avec suffixe

import type {
  Supplier,
  SupplierInsert,
  SupplierUpdate,
  SupplierCategory,
  SupplierCategoryInsert,
  SupplierCategoryUpdate,
  SupplierContact,
  SupplierContactInsert,
  SupplierContactUpdate,
  Contract,
  ContractInsert,
  ContractUpdate,
  SupplierInvoice,
  SupplierInvoiceInsert,
  SupplierInvoiceUpdate,
  SupplierInvoiceLine,
  SupplierInvoiceLineInsert,
  SupplierInvoiceLineUpdate,
  SupplierPayment,
  SupplierPaymentInsert,
  SupplierPaymentUpdate,
  SupplierPaymentAllocation,
  SupplierPaymentAllocationInsert,
  SupplierPaymentAllocationUpdate,
  Row,
  Enums,
} from '@/shared/types/database'

// Re-export des types de base (comme units/types.ts)
export type {
  Supplier,
  SupplierInsert,
  SupplierUpdate,
  SupplierCategory,
  SupplierCategoryInsert,
  SupplierCategoryUpdate,
  SupplierContact,
  SupplierContactInsert,
  SupplierContactUpdate,
  Contract,
  ContractInsert,
  ContractUpdate,
  SupplierInvoice,
  SupplierInvoiceInsert,
  SupplierInvoiceUpdate,
  SupplierInvoiceLine,
  SupplierInvoiceLineInsert,
  SupplierInvoiceLineUpdate,
  SupplierPayment,
  SupplierPaymentInsert,
  SupplierPaymentUpdate,
  SupplierPaymentAllocation,
  SupplierPaymentAllocationInsert,
  SupplierPaymentAllocationUpdate,
}

// ===== ENUMS (depuis Supabase) =====
export type VatRegime = Enums<'vat_regime'>
export type ContractType = Enums<'contract_type'>
export type ContractStatus = Enums<'contract_status'>
export type SupplierInvoiceStatus = Enums<'supplier_invoice_status'>
export type PaymentInvoiceStatus = Enums<'payment_invoice_status'>
export type SupplierPaymentStatus = Enums<'supplier_payment_status'>
export type SupplierPaymentMode = Enums<'supplier_payment_mode'>
export type BillingFrequency = Enums<'billing_frequency'>
export type SupplierContactType = Enums<'supplier_contact_type'>

// ===== TYPES AVEC RELATIONS (pattern identique à UnitWithResidence) =====

// Historique statuts contrats
export type ContractStatusHistory = Row<'contract_status_history'>

// Fournisseurs avec relations
export interface SupplierWithCategory extends Supplier {
  supplier_categories?: SupplierCategory | null
  supplier_contacts?: SupplierContact[]
}

// Contrats avec relations
export interface ContractWithSupplier extends Contract {
  suppliers?: {
    id: string
    code: string
    name: string
    email: string | null
    phone: string | null
  } | null
}

// Lignes de factures avec relations
export interface SupplierInvoiceLineWithRelations extends SupplierInvoiceLine {
  residence_chart_accounts?: {
    id: string
    custom_code: string | null
    custom_name: string | null
    chart_accounts?: {
      id: string
      code: string
      name: string
    } | null
  } | null
  distribution_keys?: {
    id: string
    name: string
    key_type: string
  } | null
}

// Factures avec relations
export interface SupplierInvoiceWithRelations extends SupplierInvoice {
  suppliers?: {
    id: string
    code: string
    name: string
  } | null
  contracts?: {
    id: string
    reference: string
    label: string
  } | null
  supplier_invoice_lines?: SupplierInvoiceLineWithRelations[]
}

// Allocations de paiements avec relations
export interface SupplierPaymentAllocationWithRelations extends SupplierPaymentAllocation {
  supplier_invoices?: {
    id: string
    invoice_number: string
    internal_reference: string
    total_ttc: number
  } | null
}

// Paiements avec relations
export interface SupplierPaymentWithSupplier extends SupplierPayment {
  suppliers?: {
    id: string
    code: string
    name: string
  } | null
  supplier_payment_allocations?: SupplierPaymentAllocationWithRelations[]
}

// ===== LABELS =====

export const VAT_REGIME_LABELS: Record<VatRegime, string> = {
  NORMAL: 'TVA applicable',
  FRANCHISE: 'Franchise en base',
  EXONERE: 'Exonéré',
}

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  RECURRING: 'Récurrent',
  ONESHOT: 'Ponctuel',
}

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Brouillon',
  ACTIVE: 'Actif',
  SUSPENDED: 'Suspendu',
  TERMINATED: 'Résilié',
  EXPIRED: 'Expiré',
}

export const CONTRACT_STATUS_COLORS: Record<ContractStatus, string> = {
  DRAFT: 'gray',
  ACTIVE: 'green',
  SUSPENDED: 'orange',
  TERMINATED: 'red',
  EXPIRED: 'gray',
}

export const SUPPLIER_INVOICE_STATUS_LABELS: Record<SupplierInvoiceStatus, string> = {
  DRAFT: 'Brouillon',
  VALIDATED: 'Validée',
  PAID: 'Payée',
  CANCELLED: 'Annulée',
}

export const SUPPLIER_INVOICE_STATUS_COLORS: Record<SupplierInvoiceStatus, string> = {
  DRAFT: 'gray',
  VALIDATED: 'blue',
  PAID: 'green',
  CANCELLED: 'red',
}

export const PAYMENT_INVOICE_STATUS_LABELS: Record<PaymentInvoiceStatus, string> = {
  UNPAID: 'Non payée',
  PARTIAL: 'Partiel',
  PAID: 'Payée',
}

export const PAYMENT_INVOICE_STATUS_COLORS: Record<PaymentInvoiceStatus, string> = {
  UNPAID: 'red',
  PARTIAL: 'orange',
  PAID: 'green',
}

export const SUPPLIER_PAYMENT_STATUS_LABELS: Record<SupplierPaymentStatus, string> = {
  PENDING: 'En attente',
  EXECUTED: 'Exécuté',
  CANCELLED: 'Annulé',
}

export const SUPPLIER_PAYMENT_STATUS_COLORS: Record<SupplierPaymentStatus, string> = {
  PENDING: 'orange',
  EXECUTED: 'green',
  CANCELLED: 'red',
}

export const SUPPLIER_PAYMENT_MODE_LABELS: Record<SupplierPaymentMode, string> = {
  VIREMENT: 'Virement',
  CHEQUE: 'Chèque',
  PRELEVEMENT: 'Prélèvement',
  CB: 'Carte bancaire',
}

export const BILLING_FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  MONTHLY: 'Mensuel',
  QUARTERLY: 'Trimestriel',
  SEMI_ANNUAL: 'Semestriel',
  ANNUAL: 'Annuel',
  ONESHOT: 'Unique',
}

export const SUPPLIER_CONTACT_TYPE_LABELS: Record<SupplierContactType, string> = {
  COMMERCIAL: 'Commercial',
  TECHNIQUE: 'Technique',
  COMPTABILITE: 'Comptabilité',
  DIRECTION: 'Direction',
}

// ===== CATÉGORIES PAR DÉFAUT =====

export const DEFAULT_SUPPLIER_CATEGORIES = [
  { code: 'ASCENSEUR', name: 'Ascensoriste', default_account_code: '615100' },
  { code: 'NETTOYAGE', name: 'Entreprise de nettoyage', default_account_code: '615200' },
  { code: 'ESPACES_VERTS', name: 'Entretien espaces verts', default_account_code: '615300' },
  { code: 'PLOMBERIE', name: 'Plombier', default_account_code: '615400' },
  { code: 'ELECTRICITE', name: 'Électricien', default_account_code: '615500' },
  { code: 'CHAUFFAGE', name: 'Chauffagiste', default_account_code: '615600' },
  { code: 'ASSURANCE', name: 'Compagnie d\'assurance', default_account_code: '616000' },
  { code: 'SYNDIC', name: 'Honoraires syndic', default_account_code: '622000' },
  { code: 'AVOCAT', name: 'Avocat / Huissier', default_account_code: '622600' },
  { code: 'ARCHITECTE', name: 'Architecte / Maître d\'œuvre', default_account_code: '622100' },
  { code: 'ENERGIE', name: 'Fournisseur énergie', default_account_code: '606100' },
  { code: 'EAU', name: 'Fournisseur eau', default_account_code: '606200' },
  { code: 'DIVERS', name: 'Autres fournisseurs', default_account_code: '615900' },
] as const

// ===== FILTRES =====

export interface SupplierFilters {
  agency_id: string
  search?: string
  category_id?: string
  is_active?: boolean
}

export interface ContractFilters {
  residence_id: string
  supplier_id?: string
  status?: ContractStatus
  contract_type?: ContractType
  search?: string
}

export interface SupplierInvoiceFilters {
  residence_id: string
  fiscal_year_id?: string
  supplier_id?: string
  status?: SupplierInvoiceStatus
  payment_status?: PaymentInvoiceStatus
  search?: string
  due_date_from?: string
  due_date_to?: string
}

export interface SupplierPaymentFilters {
  residence_id: string
  fiscal_year_id?: string
  supplier_id?: string
  status?: SupplierPaymentStatus
  payment_mode?: SupplierPaymentMode
}

// ===== TYPES POUR LES FORMULAIRES =====

export type SupplierFormData = Omit<SupplierInsert, 'id' | 'code' | 'created_at' | 'updated_at' | 'created_by'>
export type SupplierUpdateData = Omit<SupplierUpdate, 'id' | 'agency_id' | 'code' | 'created_at' | 'updated_at' | 'created_by'>

export type SupplierCategoryFormData = Omit<SupplierCategoryInsert, 'id' | 'created_at' | 'updated_at'>

export type SupplierContactFormData = Omit<SupplierContactInsert, 'id' | 'created_at' | 'updated_at'>
export type SupplierContactUpdateData = Omit<SupplierContactUpdate, 'id' | 'supplier_id' | 'created_at' | 'updated_at'>

export type ContractFormData = Omit<ContractInsert, 'id' | 'reference' | 'created_at' | 'updated_at' | 'created_by'>
export type ContractUpdateData = Omit<ContractUpdate, 'id' | 'reference' | 'residence_id' | 'supplier_id' | 'created_at' | 'updated_at' | 'created_by'>

export type SupplierInvoiceFormData = Omit<SupplierInvoiceInsert, 'id' | 'internal_reference' | 'created_at' | 'updated_at' | 'created_by' | 'transaction_id'>
export type SupplierInvoiceUpdateData = Omit<SupplierInvoiceUpdate, 'id' | 'internal_reference' | 'residence_id' | 'fiscal_year_id' | 'supplier_id' | 'created_at' | 'updated_at' | 'created_by' | 'transaction_id'>

export type SupplierInvoiceLineFormData = Omit<SupplierInvoiceLineInsert, 'id' | 'created_at'>

export type SupplierPaymentFormData = Omit<SupplierPaymentInsert, 'id' | 'created_at' | 'updated_at' | 'created_by'>

// ===== TYPES POUR CRÉATION AVEC LIGNES =====

export interface CreateSupplierInvoiceData {
  invoice: Omit<SupplierInvoiceFormData, 'amount_paid' | 'amount_remaining' | 'payment_status' | 'status'>
  lines: Omit<SupplierInvoiceLineFormData, 'invoice_id' | 'line_number'>[]
}

export interface CreateSupplierPaymentData {
  residenceId: string
  fiscalYearId: string
  supplierId: string
  paymentDate: string
  paymentMode: SupplierPaymentMode
  bankAccountId: string
  allocations: { invoice_id: string; amount: number }[]
  reference?: string
}
